import { useCallback, useRef, useState } from 'react'
import { useLightbox } from '@/hooks/useLightbox'
import { fetchFormData } from '@/components/inbox/utils'
import { useConversationList } from '@/hooks/inbox/useConversationList'
import { useSelectedConversation } from '@/hooks/inbox/useSelectedConversation'
import { useConversationActions } from '@/hooks/inbox/useConversationActions'
import { useBulkActions } from '@/hooks/inbox/useBulkActions'
import { useMessageCompose } from '@/hooks/inbox/useMessageCompose'
import { useInboxWebSocket } from '@/hooks/inbox/useInboxWebSocket'
import { useInboxSync } from '@/hooks/inbox/useInboxSync'
import type {
  InboxProps,
  ConversationListItem,
  Team,
} from '@/components/inbox/types'

export function useInboxState({
  conversations: initialConversations,
  currentAgent,
  agents,
  filter,
  statusFilter,
  teamFilter,
  channelFilter,
  stats: initialStats,
  teams: initialTeams = [],
  selectedConversation: initialSelectedConversation,
  selectedConversationId,
  messages: initialMessages,
  internalNotes: initialInternalNotes,
  hasMore: initialHasMore,
  currentPage: initialCurrentPage,
}: InboxProps) {
  // Shared state declared here and passed to sub-hooks
  const [conversations, setConversations] = useState<ConversationListItem[]>(initialConversations)
  const [stats, setStats] = useState(initialStats)
  const [teams, setTeams] = useState<Team[]>(initialTeams)
  const [selectedConversationIds, setSelectedConversationIds] = useState<Set<number>>(new Set())

  // UI panel state
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(true)
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)

  // Lightbox
  const lightbox = useLightbox()

  // Video lightbox
  const [videoLightbox, setVideoLightbox] = useState<{
    isOpen: boolean
    url: string | null
    filename: string | null
  }>({ isOpen: false, url: null, filename: null })

  const handleVideoClick = useCallback((url: string, filename: string) => {
    setVideoLightbox({ isOpen: true, url, filename })
  }, [])

  const closeVideoLightbox = useCallback(() => {
    setVideoLightbox({ isOpen: false, url: null, filename: null })
  }, [])

  // Selected conversation, messages, typing, presence
  const selected = useSelectedConversation({
    initialMessages,
    initialInternalNotes,
    initialSelectedConversation,
    selectedConversationId,
  })

  // Message compose is created before WebSocket so setMessages is available.
  // The real notifyTyping is wired via compose.notifyTypingRef after WebSocket init.
  const compose = useMessageCompose({
    selectedConversationId,
    currentAgent,
    messages: selected.messages,
    setMessages: selected.setMessages,
  })

  // Conversation list (search, filter, pagination, team filter persistence)
  const list = useConversationList({
    initialConversations,
    initialHasMore,
    initialCurrentPage,
    initialTeams,
    initialStats,
    filter,
    statusFilter,
    teamFilter,
    channelFilter,
    currentAgentId: currentAgent.id,
    conversations,
    setConversations,
    setStats,
    setTeams,
    setSelectedConversationIds,
    selectedConversationId,
    isComposingNew: compose.isComposingNew,
    cancelNewConversation: compose.cancelNewConversation,
  })

  // Bulk actions
  const bulk = useBulkActions({
    conversations,
    setConversations,
    selectedConversation: selected.selectedConversation,
    setSelectedConversation: selected.setSelectedConversation,
    setStats,
    agents,
    currentAgentId: currentAgent.id,
    statusFilter,
    filteredConversations: list.filteredConversations,
    selectedConversationIds,
    setSelectedConversationIds,
  })

  // Conversation actions (close, reopen, snooze, assign)
  const actions = useConversationActions({
    selectedConversationId,
    filteredConversations: list.filteredConversations,
    buildFilterUrl: list.buildFilterUrl,
    conversations,
    setConversations,
    setSelectedConversation: selected.setSelectedConversation,
    setStats,
    agents,
    currentAgentId: currentAgent.id,
  })

  // Authoritative sync (poke-pull): WebSocket events only signal changes;
  // unread counts, stats and team counts are fetched from the server.
  const sync = useInboxSync({
    filter,
    statusFilter,
    teamFilter,
    channelFilter,
    matchesCurrentFilter: list.matchesCurrentFilter,
    setConversations,
    setStats,
    setTeams,
  })

  // WebSocket (conversation channel + inbox channel)
  const webSocket = useInboxWebSocket({
    selectedConversationId,
    currentAgentId: currentAgent.id,
    setConversations,
    setSelectedConversation: selected.setSelectedConversation,
    setMessages: selected.setMessages,
    setInternalNotes: selected.setInternalNotes,
    setTypingUsers: selected.setTypingUsers,
    setConversationPresence: selected.setConversationPresence,
    typingTimeoutRef: selected.typingTimeoutRef,
    matchesCurrentFilter: list.matchesCurrentFilter,
    requestSync: sync.requestSync,
  })

  // Wire up the real notifyTyping from WebSocket to compose refs
  compose.notifyTypingRef.current = webSocket.notifyTyping

  // Optimistic note sending
  const noteCounterRef = useRef(0)

  function markNoteFailed(tempId: string): void {
    selected.setInternalNotes(prev => prev.map(n => n._tempId === tempId ? { ...n, _sendFailed: true } : n))
  }

  function submitNote(body: string, tempId: string, files?: File[]): void {
    if (!selectedConversationId) return
    const formData = new FormData()
    formData.append('internal_note[body]', body)
    if (files) {
      files.forEach(file => formData.append('internal_note[attachments][]', file))
    }
    const url = `/conversations/${selectedConversationId}/internal_notes`
    fetchFormData(url, formData)
      .then(res => { if (!res.ok) markNoteFailed(tempId) })
      .catch(() => markNoteFailed(tempId))
  }

  const handleSendNote = useCallback((body: string, attachments?: File[]) => {
    if (!selectedConversationId || (!body.trim() && (!attachments || attachments.length === 0))) return

    const tempId = `optimistic-note-${++noteCounterRef.current}-${Date.now()}`

    // Build optimistic attachment previews
    const optimisticAttachments = attachments?.map((file, i) => ({
      id: -(noteCounterRef.current * 100 + i),
      signed_id: '',
      filename: file.name,
      content_type: file.type,
      byte_size: file.size,
      url: file.type.startsWith('image/') && !file.type.includes('heic') && !file.type.includes('heif')
        ? URL.createObjectURL(file)
        : '',
      is_video: file.type.startsWith('video/'),
      is_image: file.type.startsWith('image/'),
    })) ?? []

    selected.setInternalNotes(prev => [...prev, {
      id: -noteCounterRef.current,
      body,
      agent: currentAgent,
      attachments: optimisticAttachments,
      created_at: new Date().toISOString(),
      _optimistic: true,
      _sendFailed: false,
      _tempId: tempId,
      _files: attachments,
    }])
    submitNote(body, tempId, attachments)
  }, [selectedConversationId, currentAgent])

  const handleRetryNote = useCallback((tempId: string) => {
    const failedNote = selected.internalNotes.find(n => n._tempId === tempId)
    if (!failedNote) return

    selected.setInternalNotes(prev => prev.map(n => n._tempId === tempId ? { ...n, _sendFailed: false } : n))
    submitNote(failedNote.body, tempId, failedNote._files)
  }, [selectedConversationId, selected.internalNotes])

  return {
    // State
    searchQuery: list.searchQuery,
    setSearchQuery: list.setSearchQuery,
    conversations,
    stats,
    teams,
    selectedConversation: selected.selectedConversation,
    messages: selected.messages,
    internalNotes: selected.internalNotes,
    typingUsers: selected.typingUsers,
    conversationPresence: selected.conversationPresence,
    filteredConversations: list.filteredConversations,
    hasMore: list.hasMore,
    isLoadingMore: list.isLoadingMore,
    loadMoreRef: list.loadMoreRef,
    selectedConversationIds: bulk.selectedConversationIds,
    isComposingNew: compose.isComposingNew,
    isDetailsPanelOpen,
    setIsDetailsPanelOpen,
    isSidebarOpen,
    setIsSidebarOpen,
    replyingToMessage: compose.replyingToMessage,
    setReplyingToMessage: compose.setReplyingToMessage,
    pendingAttachments: compose.pendingAttachments,
    setPendingAttachments: compose.setPendingAttachments,
    // Refs
    messagesEndRef: selected.messagesEndRef,
    messagesContainerRef: selected.messagesContainerRef,
    snoozeButtonRef: actions.snoozeButtonRef,
    chatEditorRef: compose.chatEditorRef,
    bodyRef: compose.bodyRef,
    draftPlainTextRef: compose.draftPlainTextRef,
    // Lightbox
    lightbox,
    videoLightbox,
    handleVideoClick,
    closeVideoLightbox,
    // Snoozepicker
    snoozePicker: actions.snoozePicker,
    // Actions
    buildFilterUrl: list.buildFilterUrl,
    handleStatusChange: list.handleStatusChange,
    handleConversationClick: list.handleConversationClick,
    handleSendMessage: compose.handleSendMessage,
    handleRetryMessage: compose.handleRetryMessage,
    saveDraft: compose.saveDraft,
    deleteMessage: compose.deleteMessage,
    handleCloseConversation: actions.handleCloseConversation,
    handleCloseWithRating: actions.handleCloseWithRating,
    handleReopenConversation: actions.handleReopenConversation,
    handleUnsnoozeConversation: actions.handleUnsnoozeConversation,
    handleAssignAgent: actions.handleAssignAgent,
    handleUnassignAgent: actions.handleUnassignAgent,
    handleBulkAssign: bulk.handleBulkAssign,
    handleBulkUnassign: bulk.handleBulkUnassign,
    handleBulkStatusChange: bulk.handleBulkStatusChange,
    toggleConversationSelection: bulk.toggleConversationSelection,
    deselectAllConversations: bulk.deselectAllConversations,
    toggleSelectAll: bulk.toggleSelectAll,
    startNewConversation: compose.startNewConversation,
    cancelNewConversation: compose.cancelNewConversation,
    notifyTyping: webSocket.notifyTyping,
    handleSendNote,
    handleRetryNote,
  }
}
